<template>
  <div class="UsuarioAceptarTerminos" v-if="aceptacionPendiente">
    <div
      class="modal fade"
      id="ModalAceptarTerminosObligatorios"
      tabindex="-1"
      aria-labelledby="ModalAceptarTerminosObligatoriosLabel"
      aria-hidden="true"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div
        class="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-xl modal-fullscreen-md-down"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5
              class="modal-title fw-bold"
              id="ModalAceptarTerminosObligatoriosLabel"
            >
              Términos, condiciones y política de privacidad
            </h5>
          </div>

          <div class="modal-body">
            <div class="alert alert-warning fs-14 mb-3">
              <strong>Debes aceptar para continuar.</strong>
              No puedes usar RiskMann hasta confirmar que leíste y aceptas estos
              documentos.
            </div>

            <ul class="nav nav-tabs" id="myTabTerminos" role="tablist">
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link active fw-bold"
                  id="terminos-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#terminos-tab-pane"
                  type="button"
                  role="tab"
                  aria-controls="terminos-tab-pane"
                  aria-selected="true"
                >
                  Términos y Condiciones
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link fw-bold"
                  id="politicas-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#politicas-tab-pane"
                  type="button"
                  role="tab"
                  aria-controls="politicas-tab-pane"
                  aria-selected="false"
                >
                  Política de Privacidad
                </button>
              </li>
            </ul>
            <div class="tab-content border border-top-0 p-3 mb-4" id="myTabTerminosContent" style="max-height: 40vh; overflow-y: auto;">
              <div
                class="tab-pane fade show active"
                id="terminos-tab-pane"
                role="tabpanel"
                aria-labelledby="terminos-tab"
                tabindex="0"
              >
                <TextoTerminosCondiciones />
              </div>
              <div
                class="tab-pane fade"
                id="politicas-tab-pane"
                role="tabpanel"
                aria-labelledby="politicas-tab"
                tabindex="0"
              >
                <TextoPoliticaPrivacidad />
              </div>
            </div>

            <div class="form-check mb-2">
              <input
                id="checkboxTerminos"
                class="form-check-input"
                type="checkbox"
                :disabled="guardando"
                v-model="aceptaTerminos"
              />
              <label class="form-check-label" for="checkboxTerminos">
                He leído y acepto los <strong>Términos y Condiciones</strong> de uso.
              </label>
            </div>
            
            <div class="form-check">
              <input
                id="checkboxPoliticas"
                class="form-check-input"
                type="checkbox"
                :disabled="guardando"
                v-model="aceptaPoliticas"
              />
              <label class="form-check-label" for="checkboxPoliticas">
                He leído y acepto la <strong>Política de Privacidad y Tratamiento de Datos</strong>.
              </label>
            </div>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="guardando"
              @click="cerrarSesion"
            >
              Cerrar sesión
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="!aceptaTerminos || !aceptaPoliticas || guardando"
              @click="aceptarTerminos"
            >
              <span v-if="guardando">Guardando...</span>
              <span v-else>Aceptar y continuar</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { auth, db } from "@/firebase";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { Modal } from "bootstrap";
import TextoTerminosCondiciones from "@/components/Usuario/TextoTerminosCondiciones.vue";
import TextoPoliticaPrivacidad from "@/components/Usuario/TextoPoliticaPrivacidad.vue";

const ID_MODAL = "ModalAceptarTerminosObligatorios";

function preferenciasEstanAceptadas(usuario) {
  if (!usuario) return false;
  if (!usuario.preferencias) return false;

  return (
    usuario.preferencias.terminosCondiciones === true &&
    usuario.preferencias.politicaPrivacidad === true
  );
}

function construirActualizacionAceptacion() {
  return {
    "preferencias.terminosCondiciones": true,
    "preferencias.politicaPrivacidad": true,
    "registro.actualizacion": serverTimestamp(),
  };
}

export default {
  name: "UsuarioAceptarTerminos",

  components: {
    TextoTerminosCondiciones,
    TextoPoliticaPrivacidad,
  },

  data() {
    return {
      aceptacionPendiente: false,
      aceptaTerminos: false,
      aceptaPoliticas: false,
      guardando: false,
      authUnsubscribe: null,
    };
  },

  watch: {
    $route() {
      this.reabrirModalSiSiguePendiente();
    },
  },

  mounted() {
    this.authUnsubscribe = auth.onAuthStateChanged(async (sesion) => {
      if (sesion) {
        await this.evaluarAceptacion(sesion);
      } else {
        this.aceptacionPendiente = false;
        this.ocultarModal();
      }
    });
  },

  beforeDestroy() {
    // App.vue lo quita en ciertas rutas (entrada, sesión de soporte...), a veces justo después de
    // montarlo: si la consulta del usuario seguía en vuelo, al volver no debe abrir el modal.
    this.destruido = true;
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
    }
    this.ocultarModal();
  },

  methods: {
    async evaluarAceptacion(sesionUsuario) {
      const sesion = sesionUsuario || auth.currentUser;
      if (!sesion) {
        this.aceptacionPendiente = false;
        return;
      }

      try {
        const usuario = await this.$store.state.obtenerUsuario(sesion.uid);
        if (this.destruido) return;
        this.aceptacionPendiente = !preferenciasEstanAceptadas(usuario);

        if (this.aceptacionPendiente) {
          this.$nextTick(() => {
            this.mostrarModal();
          });
        } else {
          this.ocultarModal();
        }
      } catch (error) {
        console.error("[AceptarTerminos] Error al evaluar aceptación:", error);
      }
    },

    obtenerInstanciaModal() {
      const elemento = document.getElementById(ID_MODAL);
      if (!elemento) return null;

      return (
        Modal.getInstance(elemento) ||
        new Modal(elemento, {
          backdrop: "static",
          keyboard: false,
        })
      );
    },

    mostrarModal() {
      // Bootstrap vuelve a pegar el modal al body si ya no está en la página: sin componente, no se abre.
      if (this.destruido) return;
      const modal = this.obtenerInstanciaModal();
      if (!modal) return;
      modal.show();
    },

    ocultarModal() {
      const elemento = document.getElementById(ID_MODAL);
      if (elemento) {
        const modal = Modal.getInstance(elemento);
        if (modal) {
          modal.hide();
        }
      }
    },

    reabrirModalSiSiguePendiente() {
      if (!this.aceptacionPendiente) return;
      this.$nextTick(() => {
        this.mostrarModal();
      });
    },

    async aceptarTerminos() {
      if (!this.aceptaTerminos || !this.aceptaPoliticas || this.guardando) return;

      const sesion = auth.currentUser;
      if (!sesion) return;

      this.guardando = true;
      try {
        await updateDoc(
          doc(db, "riskmann_usuarios", sesion.uid),
          construirActualizacionAceptacion(),
        );

        this.ocultarModal();
        this.aceptacionPendiente = false;
        this.$store.state.alerta(
          "success",
          "Términos y política de privacidad aceptados.",
        );
      } catch (error) {
        console.error(
          "[AceptarTerminos] No se pudo guardar la aceptación:",
          error,
        );
        this.$store.state.alerta(
          "error",
          "No se pudo guardar la aceptación. Inténtalo de nuevo.",
        );
      } finally {
        this.guardando = false;
      }
    },

    async cerrarSesion() {
      try {
        this.ocultarModal();
        this.aceptacionPendiente = false;
        await auth.signOut();
        this.$router.replace({ name: "Entrada" });
      } catch (error) {
        console.error("[AceptarTerminos] Error al cerrar sesión:", error);
        this.$store.state.alerta("error", "No se pudo cerrar la sesión.");
      }
    },
  },
};
</script>
